import { useEffect, useMemo, useState } from 'react';

import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';

import { useMentorshipsContext } from 'hb-react/frontoffice/apps/Mentorships/contexts/MentorshipsContext';
import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { BLOCKS } from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import oneColumnTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/OneColumnTemplate';
import twoColumnsTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/TwoColumnsTemplate';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import getMentorshipsHeaderControls from 'hb-react/shared/apps/Mentorships/utils/getMentorshipsHeaderControls';
import getMentorshipsHeaderProps from 'hb-react/shared/apps/Mentorships/utils/getMentorshipsHeaderProps';
import { useAppHeaderVariant } from 'hb-react/shared/components/DesignSystem/AppHeader';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import objectToQuery from 'hb-react/shared/utils/objectToQuery';

import MentorCard, { MentorCardLoader } from '../components/MentorCard';
import {
  fetchCustomAttributesList,
  fetchMentors,
  fetchRelationships,
  getRelationshipStatus,
  getRelationship,
} from '../utils';
import { makeFilters } from '../utils/makeFilters';

const TEMPLATES = {
  [oneColumnTemplate.id]: oneColumnTemplate,
  [twoColumnsTemplate.id]: twoColumnsTemplate,
};

const makeItemRenderer = ({ data, setData }) => {
  const { customAttributesList, relationships } = data;

  const endRelationship = (mentorId, message) => {
    const relationship = getRelationship({ relationships, mentorId });

    axios
      .put(Routes.terminate_mentorships_relationship_path(relationship.id), {
        message,
      })
      .then(() => {
        s__('Mentorships|You have successfully ended a relationship');
        const updatedRelationships = data.relationships.filter(
          (r) => r.id !== relationship.id,
        );

        setData({
          ...data,
          relationships: updatedRelationships,
        });
      })
      .catch(() => {
        toast.error(s__('Mentorships|Sorry, an error occured'));
      });
  };

  return ({ item: mentor }) => (
    <MentorCard
      customAttributesList={customAttributesList}
      mentor={mentor}
      status={getRelationshipStatus({ relationships, mentorId: mentor.id })}
      reachedMaxMentees={mentor.reached_max_mentees}
      onEndRelationship={(message) => endRelationship(mentor.id, message)}
    />
  );
};

const getFetchMentors =
  ({ config }) =>
  ({ fetchFilters, page, signal }) => {
    const getFilters = () => {
      const { location, ...otherFilters } = fetchFilters.filters;

      // Using [] inside a search params key causes issues
      // when parsing the query string so we're using () instead.
      // We do need to convert () back to [] before sending the
      // request to the backend.
      const getKey = (key) =>
        `query[${key}]`.replace('(', '[').replace(')', ']');

      return {
        ...(location && objectToQuery(location, { getKey })),
        ...objectToQuery(otherFilters, { getKey }),
      };
    };

    return fetchMentors({
      config,
      fetchFilters: fetchFilters?.filters && getFilters(),
      page,
      signal,
    });
  };

const MentorshipsIndexContainer = ({ config }) => {
  const appPageConfig = config?.custom?.page;
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const breakpoint = useBreakpoint();
  const { topicId } = useTopicContext();
  const { owner } = useMentorshipsContext();
  const { mentorApplicationConfig } = useMentorshipsContext();

  const [data, setData] = useState({
    relationships: [],
    customAttributesList: null,
  });

  const itemRenderer = useMemo(
    () => makeItemRenderer({ data, setData }),
    [data],
  );

  const filters = useMemo(
    () =>
      data.customAttributesList
        ? makeFilters({
            mentorApplicationConfig,
            customAttributesList: data.customAttributesList,
          })
        : null,
    [data.customAttributesList, mentorApplicationConfig],
  );

  const fetchAdditionalData = async () => {
    const fetchedData = await Promise.all([
      fetchRelationships({ owner }),
      fetchCustomAttributesList({ owner }),
    ]);

    setData({
      relationships: fetchedData[0].relationships,
      customAttributesList: fetchedData[1].customizable_attributes,
    });
  };

  useEffect(() => {
    fetchAdditionalData();
  }, []);

  const appHeaderProps = getMentorshipsHeaderProps({
    appPageConfig,
    currentUser,
    currentNetwork,
    topic: config.topic,
    customAttributesList: data?.customAttributesList ?? [],
  });

  const { variant } = useAppHeaderVariant(appHeaderProps);
  const { controls } = getMentorshipsHeaderControls({
    appPageConfig,
    currentUser,
    currentNetwork,
    topic: config.topic,
    customAttributesList: data?.customAttributesList ?? [],
  });

  const topBlock = topicId
    ? {
        ...BLOCKS.headerControls,
        attributes: {
          controls,
        },
      }
    : {
        ...BLOCKS.appHeader,
        attributes: {
          ...appHeaderProps,
        },
      };

  const templateId =
    config?.custom?.page?.config?.blocks_renderer_template ||
    oneColumnTemplate.id;

  const template = TEMPLATES[templateId];

  return (
    <BlocksRenderer
      templateId={template.id}
      templateComponent={template.component}
      templateZones={template.zones}
      templateProps={{
        variant,
        topicId,
        hasFilters: filters?.length > 0,
        offsetRightZone: true, // TwoColumnsTempalte-specific
      }}
      blocks={[
        topBlock,
        {
          ...BLOCKS.filters,
          attributes: {
            filters,
          },
          templateAttributes: {
            [oneColumnTemplate.id]: {
              filtersByRow: 3,
              filterRows: 1,
            },
            [twoColumnsTemplate.id]: {
              filtersByRow: breakpoint === 'm' ? 3 : 1,
              filterRows: breakpoint === 'm' ? 1 : 3,
              actionButtonsCanOverflow: true,
            },
          },
        },
        {
          id: ITEMS_QUERY_KEYS.mentorships,
          component: BLOCKS.items.component,
          attributes: {
            fetchItems: getFetchMentors({ config }),
            itemLoader: MentorCardLoader,
            itemRenderer,
            loaderLines: 2,
          },
          templateAttributes: {
            [oneColumnTemplate.id]: {
              itemsByRow: breakpoint === 'xl' ? 3 : 2,
            },
            [twoColumnsTemplate.id]: {
              itemsByRow: 2,
            },
          },
        },
      ]}
    />
  );
};

export default MentorshipsIndexContainer;
